const PAPER_COLOR = '#f6f3ec';
const ANALYSIS_SIZE = 320;
const CACHE_LIMIT = 8;
const modelCache = new Map();

const STAGE_RANGES = [
  { key: 'sketch', start: 0, end: 0.08 },
  { key: 'wash', start: 0.08, end: 0.3 },
  { key: 'form', start: 0.3, end: 0.7 },
  { key: 'refine', start: 0.7, end: 0.95 },
  { key: 'finish', start: 0.95, end: 1 },
];

function hashString(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createRandom(seedValue) {
  let seed = hashString(String(seedValue || 'mindgarden')) || 1;

  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(items, random) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
  }

  return items;
}

function waitForFrame() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

function loadReference(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;

      if (error) reject(error);
      else resolve(image);
    };

    image.crossOrigin = 'anonymous';
    image.onload = () => {
      finish(image.naturalWidth > 0 ? null : new Error('Artwork source contained no image data.'));
    };
    image.onerror = () => finish(new Error('The artwork reference could not be loaded with CORS access.'));

    const timeoutId = window.setTimeout(
      () => finish(new Error('The artwork reference took too long to load.')),
      15000
    );

    image.src = source;

    if (image.complete && image.naturalWidth > 0) {
      finish(null);
    }
  });
}

function createReferenceData(image) {
  const canvas = document.createElement('canvas');
  canvas.width = ANALYSIS_SIZE;
  canvas.height = ANALYSIS_SIZE;

  const context = canvas.getContext('2d', {
    alpha: false,
    willReadFrequently: true,
  });

  if (!context) throw new Error('Could not prepare the painting reference.');

  context.drawImage(image, 0, 0, ANALYSIS_SIZE, ANALYSIS_SIZE);

  try {
    return context.getImageData(0, 0, ANALYSIS_SIZE, ANALYSIS_SIZE);
  } catch {
    throw new Error('The artwork host did not permit pixel analysis. Open the original image or retry later.');
  }
}

function createBlurredReference(reference, radius) {
  const sourceCanvas = document.createElement('canvas');
  const blurredCanvas = document.createElement('canvas');
  sourceCanvas.width = ANALYSIS_SIZE;
  sourceCanvas.height = ANALYSIS_SIZE;
  blurredCanvas.width = ANALYSIS_SIZE;
  blurredCanvas.height = ANALYSIS_SIZE;

  const sourceContext = sourceCanvas.getContext('2d');
  const blurredContext = blurredCanvas.getContext('2d', {
    alpha: false,
    willReadFrequently: true,
  });

  if (!sourceContext || !blurredContext) {
    throw new Error('Could not prepare the painting reference.');
  }

  sourceContext.putImageData(reference, 0, 0);
  blurredContext.filter = `blur(${radius}px)`;
  blurredContext.drawImage(
    sourceCanvas,
    -radius * 2,
    -radius * 2,
    ANALYSIS_SIZE + radius * 4,
    ANALYSIS_SIZE + radius * 4
  );
  blurredContext.filter = 'none';

  try {
    return blurredContext.getImageData(
      0,
      0,
      ANALYSIS_SIZE,
      ANALYSIS_SIZE
    );
  } catch {
    throw new Error('The artwork host did not permit pixel analysis. Open the original image or retry later.');
  }
}

function samplePixel(data, x, y, radius = 0) {
  const size = ANALYSIS_SIZE;
  const centerX = Math.max(0, Math.min(size - 1, Math.round(x)));
  const centerY = Math.max(0, Math.min(size - 1, Math.round(y)));

  if (radius <= 0) {
    const offset = (centerY * size + centerX) * 4;
    return [
      data.data[offset],
      data.data[offset + 1],
      data.data[offset + 2],
    ];
  }

  let red = 0;
  let green = 0;
  let blue = 0;
  let samples = 0;

  for (let offsetY = -radius; offsetY <= radius; offsetY += radius || 1) {
    for (let offsetX = -radius; offsetX <= radius; offsetX += radius || 1) {
      const px = Math.max(0, Math.min(size - 1, centerX + offsetX));
      const py = Math.max(0, Math.min(size - 1, centerY + offsetY));
      const offset = (py * size + px) * 4;
      red += data.data[offset];
      green += data.data[offset + 1];
      blue += data.data[offset + 2];
      samples += 1;
    }
  }

  return [red / samples, green / samples, blue / samples];
}

function luminance(color) {
  return color[0] * 0.2126 + color[1] * 0.7152 + color[2] * 0.0722;
}

function gradientAt(data, x, y, radius) {
  const horizontal = luminance(samplePixel(data, x + radius, y))
    - luminance(samplePixel(data, x - radius, y));
  const vertical = luminance(samplePixel(data, x, y + radius))
    - luminance(samplePixel(data, x, y - radius));

  return {
    strength: Math.hypot(horizontal, vertical),
    angle: Math.atan2(vertical, horizontal) + Math.PI / 2,
  };
}

function colorGradientAt(data, x, y, radius) {
  const horizontal = colorDistance(
    samplePixel(data, x + radius, y),
    samplePixel(data, x - radius, y)
  );
  const vertical = colorDistance(
    samplePixel(data, x, y + radius),
    samplePixel(data, x, y - radius)
  );

  return Math.hypot(horizontal, vertical);
}

function edgePriorityAt(data, x, y, radius) {
  const gradient = gradientAt(data, x, y, radius);

  return gradient.strength
    + colorGradientAt(data, x, y, radius) * 0.55;
}

function colorDistance(first, second) {
  return Math.hypot(
    first[0] - second[0],
    first[1] - second[1],
    first[2] - second[2]
  );
}

function makeStroke(data, pass, random, index) {
  const x = random() * ANALYSIS_SIZE;
  const y = random() * ANALYSIS_SIZE;
  const color = samplePixel(data, x, y, pass.blur);
  const gradient = gradientAt(data, x, y, Math.max(1, pass.blur));
  const angle = gradient.strength > 8
    ? gradient.angle
    : random() * Math.PI * 2;
  const jitteredAngle = angle + (random() - 0.5) * pass.angleJitter;
  const directionX = Math.cos(jitteredAngle);
  const directionY = Math.sin(jitteredAngle);
  let length = pass.minLength + random() * (pass.maxLength - pass.minLength);

  for (let step = 0.35; step <= 1; step += 0.2) {
    const nextColor = samplePixel(
      data,
      x + directionX * length * step,
      y + directionY * length * step
    );

    if (colorDistance(color, nextColor) > pass.boundary) {
      length *= Math.max(0.35, step - 0.1);
      break;
    }
  }

  const bend = (random() - 0.5) * pass.bend;
  const variation = (random() - 0.5) * pass.colorJitter;
  const red = Math.max(0, Math.min(255, color[0] + variation));
  const green = Math.max(0, Math.min(255, color[1] + variation * 0.65));
  const blue = Math.max(0, Math.min(255, color[2] - variation * 0.35));
  const startX = x - directionX * length * 0.5;
  const startY = y - directionY * length * 0.5;
  const endX = x + directionX * length * 0.5;
  const endY = y + directionY * length * 0.5;
  const normalX = -directionY;
  const normalY = directionX;

  return {
    id: `${pass.stage}-${index}`,
    stage: pass.stage,
    x1: startX,
    y1: startY,
    cx: x + normalX * bend,
    cy: y + normalY * bend,
    x2: endX,
    y2: endY,
    width: pass.minWidth + random() * (pass.maxWidth - pass.minWidth),
    alpha: pass.minAlpha + random() * (pass.maxAlpha - pass.minAlpha),
    color: [Math.round(red), Math.round(green), Math.round(blue)],
    bristles: pass.bristles,
    pressure: 0.72 + random() * 0.28,
  };
}

function makePositionedStroke(data, pass, random, index, x, y) {
  const color = samplePixel(data, x, y, pass.blur);
  const gradient = gradientAt(data, x, y, Math.max(1, pass.blur));
  const angle = gradient.strength > 8
    ? gradient.angle
    : random() * Math.PI * 2;
  const jitteredAngle = angle + (random() - 0.5) * pass.angleJitter;
  const directionX = Math.cos(jitteredAngle);
  const directionY = Math.sin(jitteredAngle);
  let length = pass.minLength + random() * (pass.maxLength - pass.minLength);

  for (let step = 0.35; step <= 1; step += 0.2) {
    const forwardColor = samplePixel(
      data,
      x + directionX * length * step,
      y + directionY * length * step
    );
    const backwardColor = samplePixel(
      data,
      x - directionX * length * step,
      y - directionY * length * step
    );

    if (
      colorDistance(color, forwardColor) > pass.boundary
      || colorDistance(color, backwardColor) > pass.boundary
    ) {
      length *= Math.max(0.35, step - 0.1);
      break;
    }
  }

  const bend = (random() - 0.5) * pass.bend;
  const variation = (random() - 0.5) * pass.colorJitter;
  const red = Math.max(0, Math.min(255, color[0] + variation));
  const green = Math.max(0, Math.min(255, color[1] + variation * 0.65));
  const blue = Math.max(0, Math.min(255, color[2] - variation * 0.35));
  const startX = x - directionX * length * 0.5;
  const startY = y - directionY * length * 0.5;
  const endX = x + directionX * length * 0.5;
  const endY = y + directionY * length * 0.5;
  const normalX = -directionY;
  const normalY = directionX;

  return {
    id: `${pass.stage}-${index}`,
    stage: pass.stage,
    x1: startX,
    y1: startY,
    cx: x + normalX * bend,
    cy: y + normalY * bend,
    x2: endX,
    y2: endY,
    width: pass.minWidth + random() * (pass.maxWidth - pass.minWidth),
    alpha: pass.minAlpha + random() * (pass.maxAlpha - pass.minAlpha),
    color: [Math.round(red), Math.round(green), Math.round(blue)],
    bristles: pass.bristles,
    pressure: 0.72 + random() * 0.28,
  };
}

function createRandomLayer(reference, pass, random) {
  const candidates = [];
  const attempts = Math.ceil(pass.count * 1.45);

  for (let index = 0; index < attempts; index += 1) {
    const stroke = makeStroke(reference, pass, random, index);
    const priority = pass.stage === 'sketch'
      ? edgePriorityAt(reference, stroke.x1, stroke.y1, 1) / 255
      : 0.35 + gradientAt(
        reference,
        stroke.x1,
        stroke.y1,
        Math.max(1, pass.blur)
      ).strength / 510;

    if (random() <= Math.min(1, priority + pass.count / attempts)) {
      candidates.push(stroke);
    }
  }

  shuffle(candidates, random);
  return candidates.slice(0, pass.count);
}

function createCoverageLayer(reference, pass, random) {
  const positions = [];

  for (let y = 0; y < ANALYSIS_SIZE; y += pass.gridStep) {
    for (let x = 0; x < ANALYSIS_SIZE; x += pass.gridStep) {
      const availableWidth = Math.min(
        pass.gridStep,
        ANALYSIS_SIZE - x
      );
      const availableHeight = Math.min(
        pass.gridStep,
        ANALYSIS_SIZE - y
      );

      positions.push({
        x: Math.min(
          ANALYSIS_SIZE - 1,
          x + random() * availableWidth
        ),
        y: Math.min(
          ANALYSIS_SIZE - 1,
          y + random() * availableHeight
        ),
      });
    }
  }

  shuffle(positions, random);

  return positions.map((position, index) => (
    makePositionedStroke(
      reference,
      pass,
      random,
      index,
      position.x,
      position.y
    )
  ));
}

function createEdgeLayer(reference, pass, random) {
  const candidates = [];

  for (
    let y = pass.candidateStep * 0.5;
    y < ANALYSIS_SIZE;
    y += pass.candidateStep
  ) {
    for (
      let x = pass.candidateStep * 0.5;
      x < ANALYSIS_SIZE;
      x += pass.candidateStep
    ) {
      const jitter = pass.candidateStep * 0.42;
      const sampleX = Math.max(
        0,
        Math.min(
          ANALYSIS_SIZE - 1,
          x + (random() - 0.5) * jitter * 2
        )
      );
      const sampleY = Math.max(
        0,
        Math.min(
          ANALYSIS_SIZE - 1,
          y + (random() - 0.5) * jitter * 2
        )
      );
      const priority = edgePriorityAt(
        reference,
        sampleX,
        sampleY,
        1
      );

      if (priority >= pass.minimumPriority) {
        candidates.push({
          x: sampleX,
          y: sampleY,
          priority,
          tieBreaker: random(),
        });
      }
    }
  }

  candidates.sort(
    (first, second) =>
      second.priority - first.priority
      || first.tieBreaker - second.tieBreaker
  );

  const selected = candidates.slice(0, pass.count);
  shuffle(selected, random);

  return selected.map((position, index) => (
    makePositionedStroke(
      reference,
      pass,
      random,
      index,
      position.x,
      position.y
    )
  ));
}

async function buildModel(source, artSeed) {
  const image = await loadReference(source);
  const reference = createReferenceData(image);
  const mediumReference = createBlurredReference(reference, 2);
  const broadReference = createBlurredReference(reference, 6);
  const random = createRandom(`${source}:${artSeed}`);

  const passes = [
    {
      stage: 'sketch',
      count: 220,
      blur: 1,
      minLength: 5,
      maxLength: 17,
      minWidth: 0.45,
      maxWidth: 1.25,
      minAlpha: 0.11,
      maxAlpha: 0.28,
      angleJitter: 0.14,
      bend: 2.5,
      colorJitter: 5,
      boundary: 27,
      bristles: 1,
    },
    {
      stage: 'wash',
      count: 550,
      blur: 7,
      minLength: 17,
      maxLength: 43,
      minWidth: 10,
      maxWidth: 22,
      minAlpha: 0.18,
      maxAlpha: 0.35,
      angleJitter: 0.52,
      bend: 8,
      colorJitter: 9,
      boundary: 52,
      bristles: 2,
    },
    {
      stage: 'form',
      count: 1200,
      blur: 3,
      minLength: 8,
      maxLength: 25,
      minWidth: 3.5,
      maxWidth: 10,
      minAlpha: 0.4,
      maxAlpha: 0.7,
      angleJitter: 0.29,
      bend: 5,
      colorJitter: 7,
      boundary: 37,
      bristles: 3,
    },
    {
      stage: 'refine',
      count: 11449,
      gridStep: 3,
      blur: 0,
      minLength: 2.5,
      maxLength: 7,
      minWidth: 2.5,
      maxWidth: 4,
      minAlpha: 0.8,
      maxAlpha: 0.95,
      angleJitter: 0.16,
      bend: 1.8,
      colorJitter: 3,
      boundary: 24,
      bristles: 2,
    },
    {
      stage: 'finish',
      count: 850,
      candidateStep: 3,
      minimumPriority: 15,
      blur: 0,
      minLength: 2,
      maxLength: 6,
      minWidth: 0.6,
      maxWidth: 2,
      minAlpha: 0.76,
      maxAlpha: 0.95,
      angleJitter: 0.1,
      bend: 0.9,
      colorJitter: 2,
      boundary: 17,
      bristles: 2,
    },
  ];

  const layers = [];

  layers.push(createRandomLayer(reference, passes[0], random));
  await waitForFrame();

  layers.push(createRandomLayer(broadReference, passes[1], random));
  await waitForFrame();

  layers.push(createRandomLayer(mediumReference, passes[2], random));
  await waitForFrame();

  layers.push(createCoverageLayer(reference, passes[3], random));
  await waitForFrame();

  layers.push(createEdgeLayer(reference, passes[4], random));
  await waitForFrame();

  const strokes = [];
  const passRanges = {};

  layers.forEach((layer, index) => {
    const range = STAGE_RANGES[index];
    const startIndex = strokes.length;

    strokes.push(...layer);
    passRanges[range.key] = {
      startIndex,
      endIndex: strokes.length,
      count: layer.length,
      progressStart: range.start,
      progressEnd: range.end,
    };
  });

  return {
    source,
    artSeed,
    strokes,
    passRanges,
    analysisSize: ANALYSIS_SIZE,
    nativeWidth: image.naturalWidth,
    nativeHeight: image.naturalHeight,
  };
}

export function getPainterlyModel(source, artSeed) {
  const key = `${source}:${artSeed || 12345}`;

  if (modelCache.has(key)) {
    const cached = modelCache.get(key);
    modelCache.delete(key);
    modelCache.set(key, cached);
    return cached;
  }

  const promise = buildModel(source, artSeed).catch((error) => {
    modelCache.delete(key);
    throw error;
  });

  modelCache.set(key, promise);

  while (modelCache.size > CACHE_LIMIT) {
    modelCache.delete(modelCache.keys().next().value);
  }

  return promise;
}

function traceStroke(context, stroke, fraction, scale) {
  const clamped = Math.max(0, Math.min(1, fraction));
  if (clamped <= 0) return;

  const firstX = stroke.x1 + (stroke.cx - stroke.x1) * clamped;
  const firstY = stroke.y1 + (stroke.cy - stroke.y1) * clamped;
  const endX = (
    (1 - clamped) * (1 - clamped) * stroke.x1
    + 2 * (1 - clamped) * clamped * stroke.cx
    + clamped * clamped * stroke.x2
  );
  const endY = (
    (1 - clamped) * (1 - clamped) * stroke.y1
    + 2 * (1 - clamped) * clamped * stroke.cy
    + clamped * clamped * stroke.y2
  );

  context.beginPath();
  context.moveTo(stroke.x1 * scale, stroke.y1 * scale);
  context.quadraticCurveTo(
    firstX * scale,
    firstY * scale,
    endX * scale,
    endY * scale
  );
}

export function paintPaper(context, width, height, seedValue) {
  context.fillStyle = PAPER_COLOR;
  context.fillRect(0, 0, width, height);

  const random = createRandom(`paper:${seedValue}`);

  context.save();
  context.globalAlpha = 0.045;

  for (let index = 0; index < 700; index += 1) {
    const tone = 210 + Math.floor(random() * 28);
    context.fillStyle = `rgb(${tone + 12}, ${tone + 8}, ${tone})`;
    const size = 0.35 + random() * 1.15;
    context.fillRect(random() * width, random() * height, size, size);
  }

  context.restore();
}

export function drawPainterlyStroke(context, stroke, fraction, scale) {
  const [red, green, blue] = stroke.color;

  context.save();
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.globalCompositeOperation = 'source-over';

  const bristleCount = Math.max(1, stroke.bristles);
  for (let bristle = 0; bristle < bristleCount; bristle += 1) {
    const offset = (bristle - (bristleCount - 1) / 2) * stroke.width * 0.16;
    context.save();
    context.translate(offset * scale, -offset * scale * 0.35);
    context.strokeStyle = `rgba(${red}, ${green}, ${blue}, ${stroke.alpha / bristleCount * 1.75})`;
    context.lineWidth = Math.max(
      0.55,
      stroke.width * stroke.pressure * scale * (1 - bristle * 0.08)
    );
    traceStroke(context, stroke, fraction, scale);
    context.stroke();
    context.restore();
  }

  context.restore();
}

export function getPainterlyStage(progress) {
  if (progress >= 1) return 'Painting complete';
  if (progress < 0.08) return 'Sketching';
  if (progress < 0.3) return 'Laying color';
  if (progress < 0.7) return 'Building form';
  return 'Finishing touches';
}

export function getPainterlyProgressState(model, progress) {
  const value = Math.max(0, Math.min(1, Number(progress) || 0));

  if (value >= 1) {
    return {
      completeCount: model.strokes.length,
      partial: 0,
      currentStroke: null,
    };
  }

  const stage = STAGE_RANGES.find(
    (candidate) => value >= candidate.start && value < candidate.end
  ) || STAGE_RANGES[0];
  const range = model.passRanges[stage.key];
  const localProgress = (
    value - stage.start
  ) / (stage.end - stage.start);
  const scaledProgress = range.startIndex + localProgress * range.count;
  const completeCount = Math.min(
    range.endIndex,
    Math.floor(scaledProgress)
  );
  const partial = completeCount < range.endIndex
    ? scaledProgress - completeCount
    : 0;

  return {
    completeCount,
    partial,
    currentStroke: model.strokes[completeCount] || null,
  };
}

function getPainterlyTransform(model, width, height) {
  const scale = Math.min(width, height) / model.analysisSize;

  return {
    scale,
    offsetX: (width - model.analysisSize * scale) / 2,
    offsetY: (height - model.analysisSize * scale) / 2,
  };
}

export function drawPainterlyStrokeRange(
  context,
  model,
  startIndex,
  endIndex,
  width,
  height
) {
  const transform = getPainterlyTransform(model, width, height);

  context.save();
  context.translate(transform.offsetX, transform.offsetY);

  for (
    let index = Math.max(0, startIndex);
    index < Math.min(endIndex, model.strokes.length);
    index += 1
  ) {
    drawPainterlyStroke(
      context,
      model.strokes[index],
      1,
      transform.scale
    );
  }

  context.restore();
}

export function drawPainterlyPartialStroke(
  context,
  model,
  stroke,
  fraction,
  width,
  height
) {
  if (!stroke || fraction <= 0) return;

  const transform = getPainterlyTransform(model, width, height);

  context.save();
  context.translate(transform.offsetX, transform.offsetY);
  drawPainterlyStroke(context, stroke, fraction, transform.scale);
  context.restore();
}

export function drawPainterlyFrame(context, model, progress, width, height) {
  const state = getPainterlyProgressState(model, progress);

  paintPaper(context, width, height, model.artSeed);
  drawPainterlyStrokeRange(
    context,
    model,
    0,
    state.completeCount,
    width,
    height
  );

  const partial = state.partial;
  const completeCount = state.completeCount;

  if (partial > 0 && completeCount < model.strokes.length) {
    drawPainterlyPartialStroke(
      context,
      model,
      model.strokes[completeCount],
      partial,
      width,
      height
    );
  }

  return {
    completeCount,
    partial,
    currentStroke: model.strokes[completeCount] || null,
  };
}

export async function renderPainterlyFrame({
  source,
  artSeed,
  progress = 1,
  width,
  height,
}) {
  const model = await getPainterlyModel(source, artSeed);
  const canvas = document.createElement('canvas');
  canvas.width = width || model.nativeWidth;
  canvas.height = height || model.nativeHeight;

  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Could not prepare the painting export.');

  drawPainterlyFrame(context, model, progress, canvas.width, canvas.height);
  return canvas;
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The painted PNG could not be prepared.'));
    }, 'image/png');
  });
}